import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Button, Card, Text, TextInput, Title } from '@tremor/react';
import type { DviInspection, DviItem, DviStatus } from '../../api/types';
import { createDvi, saveDvi, completeDvi } from '../../api/dvi';
import { saveDraft, clearDraft, markPending } from '../../lib/dviOffline';
import { t } from '../../lib/i18n';

const STATUSES: { s: DviStatus; label: string; cls: string }[] = [
  { s: 'green', label: '🟢', cls: 'bg-green-100 dark:bg-green-900' },
  { s: 'yellow', label: '🟡', cls: 'bg-yellow-100 dark:bg-yellow-900' },
  { s: 'red', label: '🔴', cls: 'bg-red-100 dark:bg-red-900' },
  { s: 'na', label: '—', cls: 'bg-tremor-background-muted' },
];

export default function Capture() {
  const { sid, tnum } = useParams();
  const loc = useLocation();
  const equip = (loc.state as { equip?: number } | null)?.equip ?? 0;
  const [dvi, setDvi] = useState<DviInspection | null>(null);
  const [done, setDone] = useState<string | null>(null); // share_token when finished
  const [err, setErr] = useState<string | null>(null);
  const c = t.dvi;

  useEffect(() => {
    let alive = true;
    const s = Number(sid), tn = Number(tnum);
    createDvi(s, tn, equip)
      .then((d) => { if (alive) { setDvi(d); saveDraft(d); } })
      .catch((e: Error) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, tnum]);

  const update = (id: string, patch: Partial<DviItem>) => {
    setDvi((prev) => {
      if (!prev) return prev;
      const next = { ...prev, items: (prev.items ?? []).map((it) => (it.id === id ? { ...it, ...patch } : it)) };
      saveDraft(next);
      return next;
    });
  };

  const onPhoto = (id: string, file: File) => {
    const rdr = new FileReader();
    rdr.onload = () => update(id, { photo: String(rdr.result) });
    rdr.readAsDataURL(file);
  };

  const byCat = useMemo(() => {
    const m = new Map<string, DviItem[]>();
    for (const it of dvi?.items ?? []) { const a = m.get(it.category) ?? []; a.push(it); m.set(it.category, a); }
    return [...m.entries()];
  }, [dvi]);

  const finish = () => {
    if (!dvi) return;
    const items = (dvi.items ?? []).map((it) => ({ id: it.id, status: it.status, note: it.note, recommended: it.recommended, est_price: it.est_price, photo: it.photo }));
    saveDvi(dvi.id, items)
      .then(() => completeDvi(dvi.id))
      .then(() => { clearDraft(dvi.id); setDone(dvi.share_token ?? ''); })
      .catch((e: Error) => { markPending(dvi.id); setErr(e.message + ' — guardado local, se sincronizará'); });
  };

  if (err && !dvi) return <div className="p-6"><Text className="text-red-500">{err}</Text></div>;
  if (!dvi) return <div className="p-6"><Text>…</Text></div>;

  const shareUrl = done ? `${window.location.origin}/inspeccion/${done}` : '';

  return (
    <div className="min-h-screen p-3 space-y-3 max-w-xl mx-auto">
      <Title className="text-base">#{dvi.tnum} · {dvi.vehicle_desc || dvi.customer_name}</Title>
      {done ? (
        <Card>
          <Title className="text-base">{c.share}</Title>
          <Text className="text-sm my-2">{c.shareHint}</Text>
          <div className="break-all text-tremor-brand underline text-sm">{shareUrl}</div>
          <Button className="mt-3" onClick={() => navigator.clipboard?.writeText(shareUrl)}>{c.copyLink}</Button>
        </Card>
      ) : (
        <>
          {err && <Text className="text-amber-600 text-xs">{err}</Text>}
          {byCat.map(([cat, items]) => (
            <Card key={cat} className="p-3">
              <Title className="text-sm mb-2">{cat}</Title>
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={it.id} className="border-t border-tremor-border pt-2">
                    <div className="flex items-center justify-between gap-2">
                      <Text className="text-sm">{it.name}</Text>
                      <div className="flex gap-1">
                        {STATUSES.map((st) => (
                          <button key={st.s} aria-label={`${it.name} ${st.s}`}
                            onClick={() => update(it.id, { status: st.s })}
                            className={`px-2 py-1 rounded ${it.status === st.s ? st.cls + ' ring-2 ring-tremor-brand' : 'opacity-60'}`}>{st.label}</button>
                        ))}
                      </div>
                    </div>
                    {(it.status === 'yellow' || it.status === 'red') && (
                      <div className="mt-2 space-y-2">
                        <TextInput placeholder={c.note} value={it.note} onChange={(e) => update(it.id, { note: e.target.value })} />
                        <div className="flex items-center gap-2">
                          <label className="text-xs flex items-center gap-1">
                            <input type="checkbox" checked={it.recommended} onChange={(e) => update(it.id, { recommended: e.target.checked })} />{c.recommend}
                          </label>
                          {it.recommended && (
                            <input type="number" className="w-24 border border-tremor-border rounded px-2 py-1 bg-tremor-background" placeholder={c.estPrice}
                              value={it.est_price || ''} onChange={(e) => update(it.id, { est_price: Number(e.target.value) })} />
                          )}
                          <label className="text-xs ml-auto cursor-pointer text-tremor-brand">
                            📷 {c.photo}
                            <input type="file" accept="image/*" capture="environment" className="hidden"
                              onChange={(e) => e.target.files && e.target.files[0] && onPhoto(it.id, e.target.files[0])} />
                          </label>
                        </div>
                        {it.photo && <img src={it.photo} alt="" className="max-h-32 rounded" />}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
          <Button className="w-full" size="lg" onClick={finish}>{c.finish}</Button>
        </>
      )}
    </div>
  );
}
